import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import Users_no_row, { ReferralCard } from './users_row';
import Pagination from '@/components/ui/pagination';
import { userService } from "../../../_services";
import Refferral_pop from './ReferralLinksDialog.jsx';
import ChangePass from './ChangePasswordDialog.jsx';
import DeactivateRefferalLinks from './DeactivateReferralLinksDialog.jsx';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Search, Loader2 } from "lucide-react";
        
/**
 * Referral Links Management Page
 * Modernized with shadcn components - Manage referral link users
 */
function Refferallink_Page() {
    const searchInputRef = useRef(null);

    // State
    const [userData, setUserData] = useState([]);
    const [allUserData, setAllUserData] = useState([]);
    const [pageOfItems, setPageOfItems] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showReferralPopup, setShowReferralPopup] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showDeactivateLinks, setShowDeactivateLinks] = useState(false);

    // Selected user data
    const [selectedRepId, setSelectedRepId] = useState('');
    const [selectedUserFullname, setSelectedUserFullname] = useState('');
    const [selectedUsername, setSelectedUsername] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        getReferralLinkUsers(userId, userName, '-1');
    }, []);

    const getReferralLinkUsers = async (userId, userName, repId) => {
        setLoading(true);
        try {
            const apidata = await userService.ReferLinkUser(userId, userName, repId);
            if (apidata && apidata.length > 0) {
                setUserData(apidata);
                setAllUserData(apidata);
            }
        } catch (error) {
            console.error('Error fetching referral link users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        const searchText = (value || searchInput).toLowerCase();
        
        if (!searchText) {
            setUserData(allUserData);
            return;
        }

        const filtered = allUserData.filter((user) => {
            const userName = user.userName?.toLowerCase() || '';
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
            const email = user.email?.toLowerCase() || '';
            const groupNumber = user.groupNumber?.toLowerCase() || '';

            return (
                userName.includes(searchText) ||
                fullName.includes(searchText) ||
                email.includes(searchText) ||
                groupNumber.includes(searchText)
            );
        });

        setUserData(filtered.length > 0 ? filtered : [{}]);
    };

    const handleReferrals = ({ userId, userFullname }) => {
        setSelectedUserFullname(userFullname);
        setSelectedRepId(userId);
        setShowReferralPopup(true);
    };

    const handleChangePassword = ({ userId }) => {
        setSelectedRepId(userId);
        setShowChangePassword(true);
    };

    const closeChangePassword = () => {
        setShowChangePassword(false);
        refreshUserList();
    };

    const handleDeactivateLinks = ({ status, userId, userName }) => {
        if (status === "Approvedbyadmin") {
            setSelectedUsername(userName);
            setSelectedUserId(userId);
            setShowDeactivateLinks(true);
        } else {
            // Approve or reject referral link request
            handleApproveReferralRequest(userId, userName, status);
        }
    };

    const handleActivateDeactivate = async ({ repId, status }) => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');

        try {
            await userService.UpdateUserStatus(userId, userName, repId, status);
            refreshUserList();
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleApproveReferralRequest = async (targetUserId, targetUserName, status) => {
        try {
            await userService.Approve_referral_link_request(targetUserId, targetUserName, status);
            refreshUserList();
        } catch (error) {
            console.error('Error updating referral request:', error);
        }
    };

    const refreshUserList = () => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        getReferralLinkUsers(userId, userName, '-1');
    };

    const onChangePage = (pageOfItems) => {
        setPageOfItems(pageOfItems);
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Admin") {
        return <Navigate replace to="/page-not-found" />;
    }

    localStorage.setItem('pagename', 'referralpage');

    const dataToDisplay = searchInput.length ? userData : pageOfItems;

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Referral Links</h1>

            {/* Search */}
            <div className="flex items-center justify-between gap-4">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50" />
                    <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search users..."
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            handleSearch(e.target.value);
                        }}
                        disabled={loading}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* ── Desktop table (md+) ───────────────────────────────────────── */}
            <Card className="hidden md:block">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-primary">Full Name</TableHead>
                                            <TableHead className="text-primary">Group Number</TableHead>
                                            <TableHead className="text-primary">Email</TableHead>
                                            <TableHead className="text-primary">Referral Status</TableHead>
                                            <TableHead className="text-primary text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {dataToDisplay.map((userRow, index) => (
                                            <Users_no_row
                                                key={userRow.userId || index}
                                                index={index}
                                                {...userRow}
                                                toggle_refferal_pop={handleReferrals}
                                                ClosePopUp={handleChangePassword}
                                                activate_deactivate={handleActivateDeactivate}
                                                deactivate_refferals_links={handleDeactivateLinks}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <div className="p-4 border-t">
                                <Pagination
                                    items={userData}
                                    onChangePage={onChangePage}
                                    showInfo={true}
                                />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ── Mobile cards (<md) ────────────────────────────────────────── */}
            <div className="md:hidden space-y-3">
                {loading ? (
                    <Card>
                        <CardContent className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </CardContent>
                    </Card>
                ) : dataToDisplay.length > 0 ? (
                    dataToDisplay.map((userRow, index) => (
                        <ReferralCard
                            key={userRow.userId || index}
                            {...userRow}
                            toggle_refferal_pop={handleReferrals}
                            ClosePopUp={handleChangePassword}
                            activate_deactivate={handleActivateDeactivate}
                            deactivate_refferals_links={handleDeactivateLinks}
                        />
                    ))
                ) : (
                    <Card>
                        <CardContent className="text-center py-8 text-primary/70">
                            No records found
                        </CardContent>
                    </Card>
                )}

                {!searchInput && (
                    <div className="pt-2">
                        <Pagination
                            items={userData}
                            onChangePage={onChangePage}
                            showInfo={true}
                        />
                    </div>
                )}
            </div>

            {/* Modals */}
            <Refferral_pop
                open={showReferralPopup}
                onOpenChange={(open) => !open && setShowReferralPopup(false)}
                title={`${selectedUserFullname} Referrals`}
                repid={selectedRepId}
            />

            <ChangePass
                open={showChangePassword}
                onOpenChange={(open) => { if (!open) closeChangePassword(); }}
                repid={selectedRepId}
            />

            <DeactivateRefferalLinks
                open={showDeactivateLinks}
                onOpenChange={(open) => !open && setShowDeactivateLinks(false)}
                userName={selectedUsername}
                userId={selectedUserId}
            />
        </div>
    );
}

export default Refferallink_Page;
